import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

// MUI
import { PhotoLibraryOutlined, UndoRounded } from '@mui/icons-material';
import { Badge, Box, Button } from '@mui/material';

// common
import MuiDialog from 'common/components/MuiDialog';
import MuiLoading from 'common/components/MuiLoading';
import NotFoundBox from 'common/components/NotFoundBox';
import useIsParent from 'common/hook/useIsParent';
import useService from 'common/hook/useService';
import useSchoolYearMonthFilter from 'common/hook/useSchoolYearMonthFilter';
import { buildResourceSearchStr } from 'common/utils/schoolYearMonths';
import SchemaForm from 'common/SchemaForm/index';

// screen
import FoodReceivingRecordsDialog from 'modules/map/components/food-traceability/FoodReceivingRecordsDialog';
import FoodTraceabilityTable from 'modules/map/components/food-traceability/FoodTraceabilityTable';
import {
  FoodTraceabilityAudienceEnum,
  FoodTraceabilityFilterModel,
  FoodTraceabilityModel,
  FoodTraceabilityRow,
} from 'modules/map/components/food-traceability/types';
import { buildDayOptions } from 'modules/map/components/food-traceability/weekDays';
import { ClientModel } from 'modules/map/types';

interface Props {
  clientData: ClientModel;
  open: boolean;
  onClose: () => void;
  initialPage?: number;
  initialRowsPerPage?: number;
}

const ALL_VALUE = '' as const;

/** searchStr dạng `year-month-week-day-state-audience` (vd 2026-8-2-0-propose-student). */
const parseSearchStr = (s?: string) => {
  if (!s) return { state: undefined, audience: undefined };
  const parts = s.split('-');
  return { state: parts[4], audience: parts[5] };
};

/**
 * Đối tượng báo hàng của 1 document. Dữ liệu thật thường KHÔNG có metadata.menu_audience
 * (null) nên phải fallback sang searchStr — đó mới là nguồn chạy thực tế.
 */
const getDocAudience = (doc?: FoodTraceabilityModel): string | undefined =>
  doc?.metadata?.v20241121?.menu_audience || parseSearchStr(doc?.searchStr).audience || undefined;

/** Đối tượng đã biết → id nhãn i18n; đối tượng lạ hiển thị nguyên chuỗi BE trả về. */
const KNOWN_AUDIENCE_LABEL: Record<string, string> = {
  [FoodTraceabilityAudienceEnum.student]: 'map.foodTraceability.student',
  [FoodTraceabilityAudienceEnum.teacher]: 'map.foodTraceability.teacher',
};

const FoodTraceabilityDialog = (props: Props) => {
  const { open, onClose, clientData, initialPage, initialRowsPerPage } = props;
  const service = useService();
  const isParent = useIsParent();

  // Tháng/tuần do khung thực đơn của năm học quyết định — hook tự chọn khung mới nhất khi tải xong.
  const [filter, setFilter] = useState<FoodTraceabilityFilterModel>({
    week: ALL_VALUE,
    day: ALL_VALUE,
    audience: ALL_VALUE,
  });
  const { monthOptions, weekOptions, frame } = useSchoolYearMonthFilter(filter, setFilter);

  // Năm lấy từ tháng đã chọn, không phải dayjs().year(): học kỳ 2 nằm ở năm dương lịch sau.
  const searchStr = useMemo(
    () => buildResourceSearchStr({ monthKey: filter.month, week: filter.week }),
    [filter.month, filter.week]
  );

  const { data, isFetching } = useQuery({
    queryKey: [searchStr, clientData.operationCode, 'food-traceability'],
    queryFn: async () => {
      if (!service || !searchStr) return [] as FoodTraceabilityModel[];
      const json = await service.ClientFoodTraceabilityController_list({
        params: {
          orgId: clientData.operationCode,
          mode: 'real',
        },
        queries: {
          limit: 500,
          offset: 0,
          searchStr,
        },
      });
      return (json ?? []) as unknown as FoodTraceabilityModel[];
    },
    enabled: open && !!service && !!searchStr,
  });

  const [openReceiving, setOpenReceiving] = useState(false);

  /** Tuần cụ thể đang chọn (undefined khi đang là "Tất cả"). */
  const selectedWeek = weekOptions.find((w) => w.id === filter.week);

  /**
   * Khoảng ngày để tra ảnh báo hàng. Lấy thẳng ngày thật của khung/tuần MenuPlan trả về, KHÔNG
   * tự tính theo tháng dương lịch nữa: khung không trùng tháng (vd khung 09/2026 chạy 07/09 →
   * 04/10) nên cách tính cũ tra sai vài ngày ở đầu và cuối khung.
   */
  const receivingDateRange = useMemo(() => {
    if (filter.day !== ALL_VALUE && filter.day !== undefined) {
      return { date: String(filter.day) };
    }
    if (selectedWeek) return { date_from: selectedWeek.date_from, date_to: selectedWeek.date_to };
    if (frame) return { date_from: frame.month_start, date_to: frame.month_end };
    return null;
  }, [filter.day, selectedWeek, frame]);

  const { data: receivingRecords = [], isFetching: isFetchingReceiving } = useQuery({
    queryKey: ['food-receiving', clientData.operationCode, receivingDateRange],
    queryFn: async () => {
      if (!service || !receivingDateRange) return [];
      const json = await service.LogiController_listFoodReceivingRecords({
        queries: {
          site_code: clientData.operationCode,
          ...receivingDateRange,
          limit: 500,
          offset: 0,
        },
      });
      return json ?? [];
    },
    enabled: open && !!service && !!receivingDateRange,
  });

  /**
   * Đối tượng ("Phân loại") hiển thị theo ĐÚNG dữ liệu báo hàng của trường đang xem —
   * mỗi trường một tập khác nhau, nên KHÔNG hardcode student/teacher. Đối tượng đã biết
   * xếp trước cho ổn định thứ tự, còn lại sắp theo alphabet.
   */
  const audienceOptions = useMemo(() => {
    const found = new Set<string>();
    (data ?? []).forEach((doc) => {
      const a = getDocAudience(doc);
      if (a) found.add(a);
    });
    const known = Object.keys(KNOWN_AUDIENCE_LABEL);
    return [
      ...known.filter((a) => found.has(a)),
      ...[...found].filter((a) => !known.includes(a)).sort(),
    ];
  }, [data]);

  // Đổi tháng/tuần → tập đối tượng có thể khác; đối tượng đang chọn mà không còn thì
  // trả về "Tất cả" để tránh bảng trắng do lọc theo giá trị không tồn tại.
  useEffect(() => {
    if (filter.audience && !audienceOptions.includes(filter.audience)) {
      setFilter((f) => ({ ...f, audience: ALL_VALUE }));
    }
  }, [audienceOptions, filter.audience]);

  const rows = useMemo<FoodTraceabilityRow[]>(() => {
    if (!data) return [];
    const flat: FoodTraceabilityRow[] = [];
    let stt = 0;
    // Ngày ăn là chuỗi `YYYY-MM-DD` (lấy từ khung), khớp thẳng với item.menu_date.
    const dayFilter =
      filter.day !== ALL_VALUE && filter.day !== undefined ? String(filter.day) : undefined;

    data.forEach((doc, docIdx) => {
      const docAudience = getDocAudience(doc);

      if (filter.audience !== ALL_VALUE && filter.audience && docAudience !== filter.audience) {
        return;
      }

      const items = doc?.structuredData?.data?.items ?? [];
      items.forEach((item, idx) => {
        if (dayFilter && item.menu_date !== dayFilter) return;
        // Bỏ item không có Tên hàng hóa (selected_warehouse_food_name rỗng): dòng đó không
        // truy xuất được gì. Lọc TRƯỚC khi đánh stt để số thứ tự liền mạch.
        if (!item.selected_warehouse_food_name?.trim()) return;
        stt += 1;
        flat.push({ ...item, id: `${docIdx}-${idx}`, stt });
      });
    });
    return flat;
  }, [data, filter.audience, filter.day]);

  const renderContent = () => {
    if (isFetching) return <MuiLoading />;
    if (!rows.length) return <NotFoundBox />;
    return (
      <FoodTraceabilityTable
        rows={rows}
        customerOperationCode={clientData.operationCode}
        initialPage={initialPage}
        initialRowsPerPage={initialRowsPerPage}
      />
    );
  };

  /**
   * Ngày ăn: ĐỦ các ngày của tuần đang chọn, theo date_from/date_to mà khung thực đơn khai.
   * Chưa chọn tuần cụ thể thì rỗng — danh sách chỉ còn "Tất cả".
   */
  const dayOptions = useMemo(
    () =>
      buildDayOptions(
        selectedWeek ? { from: selectedWeek.date_from, to: selectedWeek.date_to } : null
      ),
    [selectedWeek]
  );

  // Đổi tuần/tháng → khoảng ngày khác; ngày đang chọn mà không còn trong tuần mới thì trả về
  // "Tất cả" để tránh lọc theo một ngày ngoài tuần.
  useEffect(() => {
    if (
      dayOptions.length > 0 &&
      filter.day !== ALL_VALUE &&
      filter.day !== undefined &&
      !dayOptions.some((o) => o.id === filter.day)
    ) {
      setFilter((f) => ({ ...f, day: ALL_VALUE }));
    }
  }, [dayOptions, filter.day]);

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      disablePortal
      PaperProps={{
        sx: {
          margin: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '90%',
          maxWidth: '90%',
        },
      }}
      titleAlign="center"
      title={'map.foodTraceability.title'}
    >
      <Box className="p-2 !pb-0 tablet:p-4 flex items-end gap-2 tablet:gap-4 flex-wrap">
        <Box className="flex-1 min-w-0">
          <SchemaForm<FoodTraceabilityFilterModel>
            formData={filter}
            onChange={(next) => {
              const weekChanged = next.week !== filter.week;
              const monthChanged = next.month !== filter.month;
              setFilter({
                ...next,
                day: weekChanged || monthChanged ? ALL_VALUE : next.day,
              });
            }}
            globalProps={{ noHelperText: true }}
            schema={{
              fields: ({ formProps: { intl } }) => {
                return {
                  month: {
                    mode: 'select',
                    label: <FormattedMessage id="map.month" />,
                    options: monthOptions,
                    rawOptions: true,
                    disableClearBtn: true,
                    noHelperText: true,
                    propsWrapper: { mobile: 6, tablet: 2 },
                  },
                  week: {
                    mode: 'select',
                    label: <FormattedMessage id="map.foodTraceability.mealWeek" />,
                    options: [
                      { id: ALL_VALUE, name: intl.formatMessage({ id: 'all' }) },
                      ...weekOptions.map((w) => ({ id: w.id, name: w.id })),
                    ],
                    rawOptions: true,
                    disableClearBtn: true,
                    noHelperText: true,
                    propsWrapper: { mobile: 6, tablet: 2 },
                  },
                  // Chưa chọn tuần cụ thể thì danh sách chỉ còn "Tất cả" — không khoá, không chú thích.
                  day: {
                    mode: 'select',
                    label: <FormattedMessage id="map.foodTraceability.actualEatingDate" />,
                    options: [
                      { id: ALL_VALUE, name: intl.formatMessage({ id: 'all' }) },
                      ...dayOptions,
                    ],
                    rawOptions: true,
                    disableClearBtn: true,
                    noHelperText: true,
                    propsWrapper: { mobile: 6, tablet: 2 },
                  },
                  audience: {
                    mode: 'select',
                    label: <FormattedMessage id="map.foodTraceability.audience" />,
                    options: [
                      { id: ALL_VALUE, name: intl.formatMessage({ id: 'all' }) },
                      ...audienceOptions.map((a) => ({
                        id: a,
                        name: KNOWN_AUDIENCE_LABEL[a]
                          ? intl.formatMessage({ id: KNOWN_AUDIENCE_LABEL[a] })
                          : a,
                      })),
                    ],
                    rawOptions: true,
                    disableClearBtn: true,
                    noHelperText: true,
                    propsWrapper: { mobile: 6, tablet: 2 },
                  },
                };
              },
            }}
          />
        </Box>
        {/* Phụ huynh: ẩn nút xem ảnh nhận hàng (docx §4 quyền phụ huynh) */}
        {!isParent && (
          <Box className="flex-shrink-0">
            <Badge
              badgeContent={receivingRecords.length}
              color="error"
              showZero={false}
              overlap="circular"
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Button
                variant="contained"
                color="error"
                startIcon={<PhotoLibraryOutlined />}
                onClick={() => setOpenReceiving(true)}
                disabled={isFetchingReceiving || receivingRecords.length === 0}
              >
                <FormattedMessage id="map.foodTraceability.foodReceiptImages" />
              </Button>
            </Badge>
          </Box>
        )}
      </Box>
      <Box sx={{ overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {renderContent()}
      </Box>
      <Box className="p-2 tablet:p-4 flex justify-end gap-2 tablet:gap-4">
        <Button variant="outlined" startIcon={<UndoRounded />} onClick={onClose}>
          <FormattedMessage id="back" />
        </Button>
      </Box>
      <FoodReceivingRecordsDialog
        open={openReceiving}
        onClose={() => setOpenReceiving(false)}
        isFetching={isFetchingReceiving}
        records={receivingRecords}
      />
    </MuiDialog>
  );
};

export default FoodTraceabilityDialog;
